/**
 * NF-e Badge Component
 * Shows "NF-e Integrado" badge on invoices
 */
// React import not needed with modern JSX transform
import { FileCheck, ExternalLink, Download } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatNFeAccessKey } from '../../lib/currency-br';

type NfeStatus = 'autorizada' | 'cancelada' | 'denegada' | 'pendente';

interface NfeBadgeProps {
  status?: NfeStatus;
  accessKey?: string;
  showAccessKey?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<NfeStatus, { label: string; color: string; bgColor: string }> = {
  autorizada: {
    label: 'NF-e Autorizada',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50 border-emerald-200',
  },
  cancelada: {
    label: 'NF-e Cancelada',
    color: 'text-red-700',
    bgColor: 'bg-red-50 border-red-200',
  },
  denegada: {
    label: 'NF-e Denegada',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50 border-orange-200',
  },
  pendente: {
    label: 'NF-e Pendente',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50 border-yellow-200',
  },
};

export function NfeBadge({
  status = 'autorizada',
  accessKey,
  showAccessKey = false,
  size = 'md',
  className,
}: NfeBadgeProps) {
  const config = statusConfig[status];

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className={cn('space-y-2', className)}>
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border font-medium',
          config.bgColor,
          config.color,
          sizeClasses[size]
        )}
      >
        <FileCheck className={iconSizes[size]} />
        {config.label}
      </span>

      {showAccessKey && accessKey && (
        <div className="text-xs text-gray-500">
          <span className="font-medium">Chave de Acesso:</span>
          <code className="ml-1 font-mono text-gray-600 break-all">
            {formatNFeAccessKey(accessKey)}
          </code>
        </div>
      )}
    </div>
  );
}

/**
 * NF-e Integration Badge (simpler version for lists)
 */
export function NfeIntegratedBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5',
        'bg-teal-50 text-teal-700 rounded-full border border-teal-200',
        className
      )}
    >
      <FileCheck className="h-3 w-3" />
      NF-e Integrado
    </span>
  );
}

/**
 * NF-e Actions Component - Export buttons
 */
interface NfeActionsProps {
  accessKey?: string;
  onExportXml?: () => void;
  onExportDanfe?: () => void;
  onExportSped?: () => void;
  className?: string;
}

export function NfeActions({
  accessKey: _accessKey,
  onExportXml,
  onExportDanfe,
  onExportSped,
  className,
}: NfeActionsProps) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      <button
        onClick={onExportXml}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm
                   border border-gray-300 rounded-lg hover:bg-gray-50
                   text-gray-700 transition-colors"
      >
        <Download className="h-4 w-4" />
        Exportar XML
      </button>
      <button
        onClick={onExportDanfe}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm
                   border border-gray-300 rounded-lg hover:bg-gray-50
                   text-gray-700 transition-colors"
      >
        <Download className="h-4 w-4" />
        DANFE
      </button>
      <button
        onClick={onExportSped}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm
                   border border-teal-300 rounded-lg hover:bg-teal-50
                   text-teal-700 transition-colors"
      >
        <ExternalLink className="h-4 w-4" />
        Exportar SPED
      </button>
    </div>
  );
}

/**
 * Full NF-e Detail Panel for invoice detail page
 */
interface NfeDetailPanelProps {
  number: string;
  series: string;
  accessKey: string;
  status: NfeStatus;
  protocol?: string;
  authDate?: string;
  className?: string;
}

export function NfeDetailPanel({
  number,
  series,
  accessKey,
  status,
  protocol,
  authDate,
  className,
}: NfeDetailPanelProps) {
  return (
    <div className={cn('rounded-lg border border-gray-200 p-4 space-y-4', className)}>
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">Nota Fiscal Eletrônica</h4>
          <p className="text-sm text-gray-500">
            Nº {number} | Série {series}
          </p>
        </div>
        <NfeBadge status={status} size="md" />
      </div>

      <div className="space-y-3">
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Chave de Acesso (44 dígitos)
          </span>
          <code className="block mt-1 text-sm font-mono text-gray-700 break-all bg-gray-50 p-2 rounded">
            {formatNFeAccessKey(accessKey)}
          </code>
        </div>

        {protocol && (
          <div className="flex gap-8">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Protocolo
              </span>
              <p className="text-sm text-gray-700">{protocol}</p>
            </div>
            {authDate && (
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Data Autorização
                </span>
                <p className="text-sm text-gray-700">{authDate}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <NfeActions
        accessKey={accessKey}
        onExportXml={() => console.log('Export XML')}
        onExportDanfe={() => console.log('Export DANFE')}
        onExportSped={() => console.log('Export SPED')}
      />
    </div>
  );
}

export default NfeBadge;
